import { useAtomValue, useSetAtom } from "jotai";
import { RemittanceItem } from "@/core/entities/deduction/remittance";
import {
  remittanceItemsAtom,
  errorAtom,
  isLoadingAtom,
  totalCountAtom,
  currentPageAtom,
  pageLimitAtom,
  getRemittanceItemsAtom,
  setCurrentPageAtom,
  setSortColumnAtom,
  sortColumnAtom,
  deductionCountAtom,
  getDeductionCountAtom,
  DeductionCountState,
  updateRemittanceItemAtom,
  getRemittanceItemByIdAtom,
  columnsAtom,
  summaryAtom,
} from "@/store/deduction/remittanceItem";
import { ITableSortColumn } from "@/core/interface";
import { searchRemittanceAtom, setSearchRemittanceAtom } from "@/store/deduction/main";
import { useCallback } from "react";
import { drawerInfoAtom, setDrawerInfoAtom } from "@/store/deduction/drawer";
import { IColumnAlias } from "@/core/repositories/deduction";
import { IRemittanceItemSummary } from "@/core/repositories/remittance";
import { useRefreshDeductionWorkspace } from "../useRefreshDeductionWorkspace";

interface UseRemittanceItem {
  remittanceItems: RemittanceItem[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  deductionCount: DeductionCountState;
  currentPage: number;
  currentSortColumn: ITableSortColumn | null;
  pageLimit: number;
  totalPages: number;

  columns: IColumnAlias[];
  summary: IRemittanceItemSummary[] | null | undefined;

  triggerGetRemittanceItems: (refetch?: boolean) => void;
  handlePageChange: (page: number) => void;
  resetPageNumber: () => void;
  handleSortColumnChange: (sortColumn: ITableSortColumn | null) => void;

  searchRemittance: string;
  handleSearchRemittance: (searchRemittance: string) => void;
  triggerGetDeductionCount: (refetch?: boolean) => void;
  handleUpdateRemittanceItem: (updatedRemittanceItem: RemittanceItem) => Promise<void>;
  getRemittanceItemById: (remittanceItemId: number, refetch?: boolean) => Promise<RemittanceItem>;
}

export function useRemittanceItem(): UseRemittanceItem {
  // Read-only atoms
  const remittanceItems = useAtomValue(remittanceItemsAtom);
  const totalCount = useAtomValue(totalCountAtom);
  const currentPage = useAtomValue(currentPageAtom);
  const currentSortColumn = useAtomValue(sortColumnAtom);
  const pageLimit = useAtomValue(pageLimitAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const error = useAtomValue(errorAtom);
  const deductionCount = useAtomValue(deductionCountAtom);
  const drawerInfo = useAtomValue(drawerInfoAtom);

  const totalPages = Math.ceil(totalCount / pageLimit);

  const searchRemittance = useAtomValue(searchRemittanceAtom);

  const columns = useAtomValue(columnsAtom);
  const summary = useAtomValue(summaryAtom);

  // Action atoms
  const getRemittanceItems = useSetAtom(getRemittanceItemsAtom);
  const setCurrentPage = useSetAtom(setCurrentPageAtom);
  const setSortColumn = useSetAtom(setSortColumnAtom);
  const setSearchRemittance = useSetAtom(setSearchRemittanceAtom);
  const getDeductionCount = useSetAtom(getDeductionCountAtom);
  const updateRemittanceItem = useSetAtom(updateRemittanceItemAtom);
  const getRemittanceItemById = useSetAtom(getRemittanceItemByIdAtom);
  const setDrawerInfo = useSetAtom(setDrawerInfoAtom);
  const refreshDeductionWorkspace = useRefreshDeductionWorkspace();

  // Get remittance items for the first time or refetch
  const triggerGetRemittanceItems = useCallback(
    (refetch?: boolean) => {
      if (refetch) {
        setCurrentPage(1);
      }
      getRemittanceItems(refetch);
    },
    [getRemittanceItems, setCurrentPage]
  );

  // Get deduction count for the first time or refetch
  const triggerGetDeductionCount = useCallback(
    (refetch?: boolean) => {
      if (refetch) {
        setCurrentPage(1);
      }
      getDeductionCount(refetch);
    },
    [getDeductionCount, setCurrentPage]
  );

  // Handle page change event
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    triggerGetRemittanceItems();
  };

  const resetPageNumber = () => {
    setCurrentPage(1);
  };

  // Handle sort column change event
  const handleSortColumnChange = (sortColumn: ITableSortColumn | null) => {
    setSortColumn(sortColumn);
    getRemittanceItems();
  };

  // Handle search remittance event
  const handleSearchRemittance = (value: string) => {
    setSearchRemittance(value);
    handlePageChange(1);
  };

  // Handle update remittance item event
  const handleUpdateRemittanceItem = async (updatedRemittanceItem: RemittanceItem) => {
    await updateRemittanceItem(updatedRemittanceItem);

    setDrawerInfo({
      remittanceItem: updatedRemittanceItem,
      nextPrevEnabled: drawerInfo?.nextPrevEnabled ?? true,
      remittanceItemId: updatedRemittanceItem.remittanceItemId,
      checkNumber: drawerInfo?.checkNumber as string,
      invoiceNumber: updatedRemittanceItem.invoiceNumber,
      isRemittanceItemsPage: drawerInfo?.isRemittanceItemsPage ?? false,
      purchaserKey: updatedRemittanceItem.purchaser?.purchaserKey as string,
      isDeductionIncomplete: updatedRemittanceItem.isIncompleteDeduction ?? false,
      isManuallyUploaded: updatedRemittanceItem.isManuallyUploaded ?? false,
    });

    refreshDeductionWorkspace(true);
  };

  return {
    remittanceItems,
    isLoading,
    error,
    totalCount,
    currentPage,
    currentSortColumn,
    pageLimit,
    totalPages,
    columns,
    summary,
    triggerGetRemittanceItems,
    handlePageChange,
    handleSortColumnChange,
    resetPageNumber,
    searchRemittance,
    handleSearchRemittance,
    deductionCount,
    triggerGetDeductionCount,
    handleUpdateRemittanceItem,
    getRemittanceItemById,
  };
}
